import { z } from "zod";

/**
 * Contrat de Ma timeline (spec docs/idees/timeline-vie/05-spec.md, issue #497), partage par le
 * backend, qui valide chaque corps recu et chaque reponse, et par le frontal, qui rejoue les
 * reponses a la reception. Tout est ecrit en schemas Zod, les types en sont inferes.
 *
 * Les dates sont des chaines `AAAA-MM-JJ`, sans heure ni fuseau : un souvenir ne se decale pas
 * d'un jour selon l'heure. Les dates-heures (creation, modification) sont en ISO 8601.
 *
 * Les bornes d'un point arrivent structurees (texte, debut, fin de l'intervalle, precision), ou
 * en texte seul (`startRaw`, `endRaw`) : le backend les lit alors avec `fuzzy-date.ts`
 * (ticket 3, #510), qui produit ces memes champs.
 */

// ---------------------------------------------------------------------------------------------
// Vocabulaire
// ---------------------------------------------------------------------------------------------

/** Un moment (`point`) ou une periode, avec un debut et une fin ou « en cours ». */
export const ENTRY_KINDS = ["point", "period"] as const;
export const entryKindSchema = z.enum(ENTRY_KINDS);
export type EntryKind = z.infer<typeof entryKindSchema>;

/** Qui voit un point : le proprietaire seul, tous les membres `family`, ou une liste nommee. */
export const VISIBILITIES = ["private", "family", "custom"] as const;
export const visibilitySchema = z.enum(VISIBILITIES);
export type Visibility = z.infer<typeof visibilitySchema>;

/** D'ou vient un point. `proposal` n'est pose que par l'acceptation d'une proposition (ticket 7). */
export const ENTRY_SOURCES = ["manual", "question", "concerts", "proposal"] as const;
export const entrySourceSchema = z.enum(ENTRY_SOURCES);
export type EntrySource = z.infer<typeof entrySourceSchema>;

/** Les origines qu'un client peut ecrire lui-meme : toutes sauf `proposal`. */
export const WRITABLE_SOURCES = ["manual", "question", "concerts"] as const;

/** La precision d'une date floue (spec, section 3.4). */
export const DATE_PRECISIONS = [
  "day",
  "month",
  "season",
  "year",
  "age",
  "school_year",
  "stage",
] as const;
export const datePrecisionSchema = z.enum(DATE_PRECISIONS);
export type DatePrecision = z.infer<typeof datePrecisionSchema>;

/** Les roles d'un membre de la timeline : le proprietaire, et les proches (ticket 7). */
export const MEMBER_ROLES = ["owner", "family", "close"] as const;
export const memberRoleSchema = z.enum(MEMBER_ROLES);
export type MemberRole = z.infer<typeof memberRoleSchema>;

/** Bornes de saisie, reprises par la base (CHECK de la migration 1001) et par le frontal. */
export const LIMITS = { title: 120, note: 500, raw: 80, trackName: 40, shareLabel: 60 } as const;

/** Le plus de pistes qu'une timeline porte (par defaut et ajoutees, masquees comprises). */
export const MAX_TRACKS = 30;

/**
 * Les pistes creees avec la timeline, dans leur ordre. Jamais de piste sante (spec, section 4).
 * Les couleurs sont en majuscules : la base les verifie ainsi.
 */
export const DEFAULT_TRACKS = [
  { slug: "amour", name: "Amour", color: "#FF8A65" },
  { slug: "famille", name: "Famille", color: "#7FA8FF" },
  { slug: "etudes", name: "Études", color: "#6FD3A0" },
  { slug: "travail", name: "Travail", color: "#B79CFF" },
  { slug: "voyages", name: "Voyages", color: "#5ED3E6" },
  { slug: "musique", name: "Musique", color: "#FF7EB6" },
  { slug: "maisons", name: "Maisons", color: "#F2C14E" },
  { slug: "amis", name: "Amis", color: "#A8D672" },
] as const;

// ---------------------------------------------------------------------------------------------
// Les dates
// ---------------------------------------------------------------------------------------------

/** Vrai pour une date `AAAA-MM-JJ` qui existe au calendrier (pas de 31 avril, ni de 29 fevrier 2023). */
export function isIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < 1) return false;
  // `setUTCFullYear` et non `Date.UTC`, qui lit une annee de 0 a 99 comme 1900 a 1999.
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

export const isoDateSchema = z
  .string()
  .refine(isIsoDate, "Date attendue au format AAAA-MM-JJ, et qui existe au calendrier.");

/** Un texte facultatif : rogne, et vide vaut absent (`null`). */
const optionalText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} : ${String(max)} caractères au plus.`)
    .nullable()
    .transform((value) => (value === null || value === "" ? null : value));

/**
 * Les bornes donnees en texte seul (`startRaw` sans debut, fin ni precision) : le backend les
 * lit avec `parseFuzzyDate` avant de verifier la coherence du point.
 */
export function rawOnlyBounds(
  entry: Pick<
    EntryDates,
    | "startRaw"
    | "startFrom"
    | "startTo"
    | "startPrecision"
    | "endRaw"
    | "endFrom"
    | "endTo"
    | "endPrecision"
  >,
): ("start" | "end")[] {
  return (["start", "end"] as const).filter(
    (side) =>
      entry[`${side}Raw`] !== null &&
      entry[`${side}From`] === null &&
      entry[`${side}To`] === null &&
      entry[`${side}Precision`] === null,
  );
}

/** Les champs de date d'un point, tels que la base les range. */
export type EntryDates = {
  kind: EntryKind;
  startRaw: string | null;
  startFrom: string | null;
  startTo: string | null;
  startPrecision: DatePrecision | null;
  endRaw: string | null;
  endFrom: string | null;
  endTo: string | null;
  endPrecision: DatePrecision | null;
  ongoing: boolean;
  undated: boolean;
};

export type DateIssue = { path: string; message: string };

/**
 * Les incoherences des dates d'un point, une par regle enfreinte ; une liste vide veut dire
 * coherent. La base porte les memes regles en CHECK (migration 1001) : ici, le message lisible.
 *
 * - une borne est absente (tout nul) ou complete (debut, fin, precision ; le texte est facultatif),
 *   et son debut ne passe pas sa fin ;
 * - a dater : aucune borne, pas « en cours » ; date : un debut ;
 * - un point n'a ni fin ni « en cours » ;
 * - une periode datee a une fin ou est « en cours », jamais les deux, et sa fin ne precede pas
 *   son debut.
 *
 * Les chaines `AAAA-MM-JJ` se comparent comme des dates.
 */
export function entryDateIssues(entry: EntryDates): DateIssue[] {
  const issues: DateIssue[] = [];
  const bound = (side: "start" | "end") => {
    const raw = entry[`${side}Raw`];
    const from = entry[`${side}From`];
    const to = entry[`${side}To`];
    const precision = entry[`${side}Precision`];
    const any = raw !== null || from !== null || to !== null || precision !== null;
    const complete = from !== null && to !== null && precision !== null;
    if (any && !complete) {
      issues.push({
        path: `${side}From`,
        message: `Une borne de date demande son début, sa fin et sa précision (${side}).`,
      });
    }
    if (from !== null && to !== null && from > to) {
      issues.push({
        path: `${side}To`,
        message: `La fin de l'intervalle précède son début (${side}).`,
      });
    }
    return any;
  };
  const hasStart = bound("start");
  const hasEnd = bound("end");

  if (entry.undated) {
    if (hasStart || hasEnd || entry.ongoing) {
      issues.push({
        path: "undated",
        message: "Un point à dater n'a ni date ni « en cours ».",
      });
    }
  } else if (!hasStart) {
    issues.push({ path: "startFrom", message: "Un point daté demande un début." });
  }

  if (entry.kind === "point") {
    if (hasEnd || entry.ongoing) {
      issues.push({ path: "kind", message: "Un moment n'a ni fin ni « en cours »." });
    }
  } else {
    if (entry.ongoing && hasEnd) {
      issues.push({ path: "ongoing", message: "Une période en cours n'a pas de fin." });
    }
    if (!entry.undated && !entry.ongoing && !hasEnd) {
      issues.push({
        path: "endFrom",
        message: "Une période datée demande une fin, ou d'être en cours.",
      });
    }
    if (entry.startFrom !== null && entry.endTo !== null && entry.startFrom > entry.endTo) {
      issues.push({ path: "endTo", message: "La fin de la période précède son début." });
    }
  }
  return issues;
}

// ---------------------------------------------------------------------------------------------
// Les corps recus
// ---------------------------------------------------------------------------------------------

/** Les champs qu'un client ecrit sur un point, tous facultatifs (la modification). */
const entryFields = {
  kind: entryKindSchema,
  title: z
    .string()
    .trim()
    .min(1, "Le titre est vide.")
    .max(LIMITS.title, `Titre : ${String(LIMITS.title)} caractères au plus.`),
  note: optionalText(LIMITS.note, "Note"),
  trackId: z.number().int().positive().max(2_147_483_647),
  startRaw: optionalText(LIMITS.raw, "Date saisie"),
  startFrom: isoDateSchema.nullable(),
  startTo: isoDateSchema.nullable(),
  startPrecision: datePrecisionSchema.nullable(),
  endRaw: optionalText(LIMITS.raw, "Date saisie"),
  endFrom: isoDateSchema.nullable(),
  endTo: isoDateSchema.nullable(),
  endPrecision: datePrecisionSchema.nullable(),
  ongoing: z.boolean(),
  undated: z.boolean(),
  visibility: visibilitySchema,
  source: z.enum(WRITABLE_SOURCES),
};

/** Le contenu d'un point a la creation, hors visibilite et origine : aussi celui d'une proposition. */
const entryContentShape = {
  kind: entryFields.kind,
  title: entryFields.title,
  note: entryFields.note.optional().transform((value) => value ?? null),
  trackId: entryFields.trackId,
  startRaw: entryFields.startRaw.optional().transform((value) => value ?? null),
  startFrom: entryFields.startFrom.optional().transform((value) => value ?? null),
  startTo: entryFields.startTo.optional().transform((value) => value ?? null),
  startPrecision: entryFields.startPrecision.optional().transform((value) => value ?? null),
  endRaw: entryFields.endRaw.optional().transform((value) => value ?? null),
  endFrom: entryFields.endFrom.optional().transform((value) => value ?? null),
  endTo: entryFields.endTo.optional().transform((value) => value ?? null),
  endPrecision: entryFields.endPrecision.optional().transform((value) => value ?? null),
  ongoing: entryFields.ongoing.default(false),
  undated: entryFields.undated.default(false),
};

/** Les dates d'un corps de creation, sauf si une borne est en texte seul (lue par le serveur). */
function checkBodyDates(entry: EntryDates, ctx: z.RefinementCtx): void {
  if (rawOnlyBounds(entry).length > 0) return;
  for (const issue of entryDateIssues(entry)) {
    ctx.addIssue({ code: "custom", path: [issue.path], message: issue.message });
  }
}

/** Le plus de proches qu'on peut nommer sur un point. */
export const MAX_VIEWERS = 200;

/** Les proches nommes d'un point « Des proches choisis » : des identifiants de membres. */
export const viewersSchema = z
  .array(z.number().int().positive().max(2_147_483_647))
  .max(MAX_VIEWERS, `${String(MAX_VIEWERS)} proches au plus.`);

const viewersOutsideCustom = "Les proches choisis ne valent que pour « Des proches choisis ».";

/**
 * La creation d'un point. Un champ inconnu est refuse (une faute de frappe ne passe pas en
 * silence). Par defaut : sans note, sans fin, pas en cours, date, prive, saisi a la main, sans
 * proche nomme. Une borne donnee en texte seul (`rawOnlyBounds`) se lit cote serveur : la
 * coherence du point se verifie alors apres cette lecture, par le backend. `viewers` (ticket 7)
 * ne vaut que pour la visibilite `custom`.
 */
export const createEntryBodySchema = z
  .strictObject({
    ...entryContentShape,
    visibility: entryFields.visibility.default("private"),
    source: entryFields.source.default("manual"),
    viewers: viewersSchema.default([]),
  })
  .superRefine((entry, ctx) => {
    if (entry.viewers.length > 0 && entry.visibility !== "custom") {
      ctx.addIssue({ code: "custom", path: ["viewers"], message: viewersOutsideCustom });
    }
    checkBodyDates(entry, ctx);
  });
export type CreateEntryBody = z.infer<typeof createEntryBodySchema>;

/**
 * La modification d'un point : les memes champs, tous facultatifs, au moins un. La coherence
 * des dates se verifie sur le point APRES fusion avec ce que la base a (le backend le fait),
 * puisqu'un seul champ peut la rompre.
 */
export const updateEntryBodySchema = z
  .strictObject({
    kind: entryFields.kind.optional(),
    title: entryFields.title.optional(),
    note: entryFields.note.optional(),
    trackId: entryFields.trackId.optional(),
    startRaw: entryFields.startRaw.optional(),
    startFrom: entryFields.startFrom.optional(),
    startTo: entryFields.startTo.optional(),
    startPrecision: entryFields.startPrecision.optional(),
    endRaw: entryFields.endRaw.optional(),
    endFrom: entryFields.endFrom.optional(),
    endTo: entryFields.endTo.optional(),
    endPrecision: entryFields.endPrecision.optional(),
    ongoing: entryFields.ongoing.optional(),
    undated: entryFields.undated.optional(),
    visibility: entryFields.visibility.optional(),
    source: entryFields.source.optional(),
    viewers: viewersSchema.optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, "Rien à modifier.");
export type UpdateEntryBody = z.infer<typeof updateEntryBodySchema>;

// ---------------------------------------------------------------------------------------------
// Les reponses
// ---------------------------------------------------------------------------------------------

export const trackSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string(),
  name: z.string(),
  color: z.string(),
  position: z.number().int().nonnegative(),
  isDefault: z.boolean(),
});
export type Track = z.infer<typeof trackSchema>;

export const entrySchema = z.object({
  id: z.number().int().positive(),
  trackId: z.number().int().positive(),
  kind: entryKindSchema,
  title: z.string(),
  note: z.string().nullable(),
  startRaw: z.string().nullable(),
  startFrom: z.string().nullable(),
  startTo: z.string().nullable(),
  startPrecision: datePrecisionSchema.nullable(),
  endRaw: z.string().nullable(),
  endFrom: z.string().nullable(),
  endTo: z.string().nullable(),
  endPrecision: datePrecisionSchema.nullable(),
  ongoing: z.boolean(),
  undated: z.boolean(),
  visibility: visibilitySchema,
  source: entrySourceSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Les proches nommes (visibilite `custom`). Rendu au proprietaire seul. */
  viewers: z.array(z.number().int().positive()).optional(),
  /** Le proche dont la proposition a donne ce point. Rendu au proprietaire seul. */
  proposedByMemberId: z.number().int().positive().nullable().optional(),
});
export type Entry = z.infer<typeof entrySchema>;

/**
 * La frise : les pistes dans leur ordre, les points tries par debut (les « a dater » a la fin).
 * Pour un proche (ticket 7), seulement ce qui lui est ouvert : les points, le compteur « a dater »
 * et les pistes en sont calcules. `role` dit au frontal quel ecran montrer, `ownerName` le nom
 * du proprietaire (s'il en a un) pour la vue d'un proche.
 */
export const friseSchema = z.object({
  role: memberRoleSchema,
  ownerName: z.string().nullable(),
  tracks: z.array(trackSchema),
  entries: z.array(entrySchema),
  undatedCount: z.number().int().nonnegative(),
});
export type Frise = z.infer<typeof friseSchema>;

// ---------------------------------------------------------------------------------------------
// Les dates floues et les reglages (ticket 3, #510)
// ---------------------------------------------------------------------------------------------

/** Le corps de `POST /api/t/dates/parse` : le texte tape, pour l'apercu. */
export const parseDateBodySchema = z.strictObject({
  raw: z.string().max(LIMITS.raw, `Date saisie : ${String(LIMITS.raw)} caractères au plus.`),
});
export type ParseDateBody = z.infer<typeof parseDateBodySchema>;

/** Le resultat de `parseFuzzyDate`, tel que la route le rend (toujours en 200). */
export const parsedDateSchema = z.discriminatedUnion("ok", [
  z.object({
    ok: z.literal(true),
    value: z.object({
      raw: z.string(),
      from: z.string(),
      to: z.string(),
      precision: datePrecisionSchema,
    }),
    label: z.string(),
    caveat: z.string().optional(),
  }),
  z.object({
    ok: z.literal(false),
    reason: z.enum(["empty", "unknown", "needs_birth_date", "invalid"]),
    message: z.string(),
  }),
]);
export type ParsedDate = z.infer<typeof parsedDateSchema>;

/** Les reglages de la timeline. */
export const settingsSchema = z.object({ birthDate: z.string().nullable() });
export type Settings = z.infer<typeof settingsSchema>;

/** La modification des reglages ; la borne « pas apres aujourd'hui » se verifie au serveur. */
export const updateSettingsBodySchema = z.strictObject({ birthDate: isoDateSchema.nullable() });
export type UpdateSettingsBody = z.infer<typeof updateSettingsBodySchema>;

export const MIN_BIRTH_DATE = "1900-01-01";

/** Le message du refus d'une date de naissance hors bornes, ou `null` si elle est admise. */
export function birthDateIssue(birthDate: string, today: string): string | null {
  if (birthDate < MIN_BIRTH_DATE)
    return "La date de naissance ne peut pas précéder le 1er janvier 1900.";
  if (birthDate > today) return "La date de naissance ne peut pas être dans le futur.";
  return null;
}

// ---------------------------------------------------------------------------------------------
// Les proches et les propositions (ticket 7, #514)
// ---------------------------------------------------------------------------------------------

/** Les cercles d'un proche invite : la famille, ou un proche choisi au cas par cas. */
export const INVITED_ROLES = ["family", "close"] as const;
export const invitedRoleSchema = z.enum(INVITED_ROLES);
export type InvitedRole = z.infer<typeof invitedRoleSchema>;

/** Un proche, tel que le proprietaire le voit sur `/proches`. */
export const memberSchema = z.object({
  id: z.number().int().positive(),
  email: z.string(),
  role: invitedRoleSchema,
  invitedAt: z.string(),
  /** Nul tant que le proche a acces ; la date du retrait sinon. */
  revokedAt: z.string().nullable(),
  /** Vrai des que le proche s'est connecte une fois. */
  hasLoggedIn: z.boolean(),
  /** Le nombre de points qu'il voit (zero une fois retire). */
  visibleCount: z.number().int().nonnegative(),
});
export type Member = z.infer<typeof memberSchema>;

export const membersSchema = z.object({ members: z.array(memberSchema) });
export type Members = z.infer<typeof membersSchema>;

/** L'invitation d'une adresse. Aucun courrier ne part : le proche demande lui-meme son lien. */
export const inviteBodySchema = z.strictObject({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(320, "Adresse : 320 caractères au plus.")
    .pipe(z.email("Adresse e-mail invalide.")),
  role: invitedRoleSchema,
});
export type InviteBody = z.infer<typeof inviteBodySchema>;

/**
 * Une proposition d'un proche : le contenu d'un point (memes champs et memes regles de dates),
 * sans visibilite ni origine. Les dates en texte seul se lisent avec la date de naissance du
 * proprietaire de la timeline.
 */
export const proposalBodySchema = z
  .strictObject(entryContentShape)
  .superRefine((entry, ctx) => checkBodyDates(entry, ctx));
export type ProposalBody = z.infer<typeof proposalBodySchema>;

export const proposalSchema = z.object({
  id: z.number().int().positive(),
  memberId: z.number().int().positive(),
  memberEmail: z.string(),
  memberRole: invitedRoleSchema,
  trackId: z.number().int().positive(),
  kind: entryKindSchema,
  title: z.string(),
  note: z.string().nullable(),
  startRaw: z.string().nullable(),
  startFrom: z.string().nullable(),
  startTo: z.string().nullable(),
  startPrecision: datePrecisionSchema.nullable(),
  endRaw: z.string().nullable(),
  endFrom: z.string().nullable(),
  endTo: z.string().nullable(),
  endPrecision: datePrecisionSchema.nullable(),
  ongoing: z.boolean(),
  undated: z.boolean(),
  createdAt: z.string(),
});
export type Proposal = z.infer<typeof proposalSchema>;

export const proposalsSchema = z.object({ proposals: z.array(proposalSchema) });
export type Proposals = z.infer<typeof proposalsSchema>;

/**
 * L'acceptation : `{}` garde la proposition telle quelle (point prive), `{ entry }` la remplace
 * par le point modifie par le proprietaire (memes champs qu'une creation, visibilite comprise).
 */
export const acceptProposalBodySchema = z.strictObject({
  entry: createEntryBodySchema.optional(),
});
export type AcceptProposalBody = z.infer<typeof acceptProposalBodySchema>;

// ---------------------------------------------------------------------------------------------
// La gestion des pistes (Reglages > Pistes, #588)
// ---------------------------------------------------------------------------------------------

/**
 * La palette fermee des couleurs de piste : les huit des pistes par defaut, puis quatre autres.
 * En majuscules, comme la base le verifie (CHECK `^#[0-9A-F]{6}$` de la migration 1001). Une
 * couleur libre n'est pas admise : toutes tiennent sur le fond sombre du theme.
 */
export const TRACK_PALETTE = [
  "#FF8A65",
  "#7FA8FF",
  "#6FD3A0",
  "#B79CFF",
  "#5ED3E6",
  "#FF7EB6",
  "#F2C14E",
  "#A8D672",
  "#E57373",
  "#4DB6AC",
  "#9FA8DA",
  "#C5A880",
] as const;
export const trackColorSchema = z.enum(TRACK_PALETTE, {
  error: "Couleur hors de la palette.",
});
export type TrackColor = z.infer<typeof trackColorSchema>;

const trackNameSchema = z
  .string()
  .trim()
  .min(1, "Le nom est vide.")
  .max(LIMITS.trackName, `Nom : ${String(LIMITS.trackName)} caractères au plus.`);

/** Ajoute une piste : un nom et une couleur de la palette. Jamais par defaut, jamais de sante. */
export const createTrackBodySchema = z.strictObject({
  name: trackNameSchema,
  color: trackColorSchema,
});
export type CreateTrackBody = z.infer<typeof createTrackBodySchema>;

/** Renomme, recolore, masque ou affiche une piste : au moins un champ. */
export const updateTrackBodySchema = z
  .strictObject({
    name: trackNameSchema.optional(),
    color: trackColorSchema.optional(),
    hidden: z.boolean().optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, "Rien à modifier.");
export type UpdateTrackBody = z.infer<typeof updateTrackBodySchema>;

/** Le nouvel ordre : tous les identifiants de pistes de la timeline, une fois chacun. */
export const reorderTracksBodySchema = z.strictObject({
  ids: z
    .array(z.number().int().positive().max(2_147_483_647))
    .min(1, "Aucune piste.")
    .max(MAX_TRACKS, `${String(MAX_TRACKS)} pistes au plus.`),
});
export type ReorderTracksBody = z.infer<typeof reorderTracksBodySchema>;

/**
 * Une piste vue par son proprietaire dans les Reglages : masquees comprises, avec ce qu'elle
 * porte. Une piste qui porte un point ou une proposition ne se supprime pas (on la masque).
 */
export const managedTrackSchema = trackSchema.extend({
  hidden: z.boolean(),
  entryCount: z.number().int().nonnegative(),
  proposalCount: z.number().int().nonnegative(),
});
export type ManagedTrack = z.infer<typeof managedTrackSchema>;

export const managedTracksSchema = z.object({ tracks: z.array(managedTrackSchema) });
export type ManagedTracks = z.infer<typeof managedTracksSchema>;
